import axios from 'axios';

// Optimized API Client with caching and request deduplication
class OptimizedAPIClient {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'https://centeo-backend.onrender.com/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add timestamp for cache busting if needed
        if (config.method === 'get') {
          config.metadata = { startTime: new Date() };
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        // Log performance
        if (response.config.metadata) {
          const duration = new Date() - response.config.metadata.startTime;
          console.log(`API ${response.config.url} took ${duration}ms`);
        }
        return response;
      },
      (error) => {
        console.error('API Error:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  // GET with caching
  async get(url, config = {}) {
    const cacheKey = `GET:${url}:${JSON.stringify(config.params || {})}`;
    const cacheTTL = config.cacheTTL || 300000; // 5 minutes default

    // Check cache first
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < cacheTTL) {
        console.log(`Cache hit for ${url}`);
        return cached.data;
      } else {
        this.cache.delete(cacheKey);
      }
    }

    // Check for pending request
    if (this.pendingRequests.has(cacheKey)) {
      console.log(`Deduplicating request for ${url}`);
      return this.pendingRequests.get(cacheKey);
    }

    // Make request
    const requestPromise = this.client.get(url, config)
      .then(response => {
        // Return the response data directly since backend already provides the correct structure
        const result = response.data;
        
        // Cache successful responses
        this.cache.set(cacheKey, {
          data: result,
          timestamp: Date.now()
        });
        
        return result;
      })
      .catch(error => {
        const apiError = new Error(error.response?.data?.message || error.message);
        apiError.success = false;
        apiError.status = error.response?.status || 500;
        throw apiError;
      })
      .finally(() => {
        this.pendingRequests.delete(cacheKey);
      });

    this.pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }

  // POST without caching
  async post(url, data, config = {}) {
    try {
      const response = await this.client.post(url, data, config);
      // Return the response data directly since backend already provides the correct structure
      return response.data;
    } catch (error) {
      const apiError = new Error(error.response?.data?.message || error.message);
      apiError.success = false;
      apiError.status = error.response?.status || 500;
      throw apiError;
    }
  }

  // Clear cache
  clearCache(pattern) {
    if (pattern) {
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      }
    } else {
      this.cache.clear();
    }
  }

  // Get cache stats
  getCacheStats() {
    return {
      size: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

const apiClient = new OptimizedAPIClient();

export default apiClient;